/**
 * Shared formatting and status helpers for the admin loan screens.
 *
 * Status keys are normalized (upper snake case), so `Pending Review`,
 * `pending-review` and `PENDING_REVIEW` all resolve to the same entry. Values
 * that aren't listed fall back to a neutral chip with the value humanized.
 */
export const STATUS_CONFIG = {
  PENDING_REVIEW: { label: "Pending", chipKey: "pending", reviewable: true },
  AWAITING_ADMIN_SIGNOFF: {
    label: "Awaiting Sign-Off",
    chipKey: "infoDeep",
    signable: true,
  },
  AWAITING_SIGNOFF: {
    label: "Awaiting Sign-Off",
    chipKey: "infoDeep",
    signable: true,
  },
  SIGNATURE_REQUIRED: {
    label: "Awaiting Sign-Off",
    chipKey: "infoDeep",
    signable: true,
  },
  APPROVED: { label: "Approved", chipKey: "success" },
  ACTIVE: { label: "Active", chipKey: "success" },
  REPAID: { label: "Repaid", chipKey: "slate" },
  REJECTED: { label: "Rejected", chipKey: "error" },
  DRAFT: { label: "Draft", chipKey: "neutral" },
};

export const normalizeStatus = (status) =>
  String(status || "")
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, "_");

/** `PENDING_REVIEW` → `Pending Review`, for statuses not in STATUS_CONFIG. */
export const humanizeStatus = (status) =>
  normalizeStatus(status)
    .split("_")
    .filter(Boolean)
    .map((w) => w.charAt(0) + w.slice(1).toLowerCase())
    .join(" ") || "—";

export const statusConfig = (status) => STATUS_CONFIG[normalizeStatus(status)] || null;

export const statusLabel = (status) =>
  statusConfig(status)?.label || humanizeStatus(status);

export const formatSar = (value, currency = "SAR") => {
  const num = Number(value);
  if (!Number.isFinite(num)) return "—";
  return `${currency} ${num.toLocaleString("en-US", { maximumFractionDigits: 2 })}`;
};

export const formatPercent = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? `${num}%` : "—";
};

/** `Jul 31, 2026` */
export const formatApplied = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

/** `November 10, 2024` — the long form used in the details timeline. */
export const formatLongDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
};

export const formatTerm = (months) =>
  Number.isFinite(Number(months)) ? `${months} months` : "—";
